import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  LAWYER_OUTREACH_REPLIES_OPENED_EVENT,
  countUnreadLawyerOutreachReplies,
  getLawyerOutreachRepliesLastOpenedAt,
  setLawyerOutreachRepliesLastOpenedAt,
  type LawyerOutreachReplyForUnread,
} from '@/lib/lawyerOutreachRepliesStorage';

export function useLawyerOutreachUnreadReplies(
  replies: LawyerOutreachReplyForUnread[] | undefined,
) {
  const [lastOpenedAt, setLastOpenedAt] = useState<string | null>(() =>
    getLawyerOutreachRepliesLastOpenedAt(),
  );

  useEffect(() => {
    const onOpened = (event: Event) => {
      const detail = (event as CustomEvent<string>).detail;
      setLastOpenedAt(detail ?? getLawyerOutreachRepliesLastOpenedAt());
    };
    window.addEventListener(LAWYER_OUTREACH_REPLIES_OPENED_EVENT, onOpened);
    return () => {
      window.removeEventListener(LAWYER_OUTREACH_REPLIES_OPENED_EVENT, onOpened);
    };
  }, []);

  const unreadCount = useMemo(
    () => countUnreadLawyerOutreachReplies(replies ?? [], lastOpenedAt),
    [replies, lastOpenedAt],
  );

  const markRepliesOpened = useCallback(() => {
    const at = new Date().toISOString();
    setLawyerOutreachRepliesLastOpenedAt(at);
    setLastOpenedAt(at);
  }, []);

  return {
    unreadCount,
    hasUnread: unreadCount > 0,
    lastOpenedAt,
    markRepliesOpened,
  };
}
